/**
 * Airtable Batching Module
 * 
 * Implements batching functionality for Airtable operations
 * Supports grouping operations into batches of up to 10 records
 * Handles create, update, and upsert operations
 */

import { airtableConfig, ENDPOINTS } from './config';
import { invalidateCache } from './cache';
import { AirtableRecord, AirtableResponse } from './rest-api';
import { respectRateLimit } from './rate-limit';
import { AirtableError, AirtableErrorType } from './types';
import {
  processApiResponse,
  logAirtableError,
  wrapError,
  isAirtableError,
  getUserFriendlyMessage
} from './errors';
import { withRetry } from './retry';

// Maximum records per batch as per Airtable API limits
const MAX_RECORDS_PER_BATCH = 10;

/**
 * Error for batch operations
 */
export class BatchOperationError extends AirtableError {
  records: Array<{ id?: string; error: Error }>;
  
  constructor(message: string, records: Array<{ id?: string; error: Error }>, context?: Record<string, any>) {
    super(
      message,
      AirtableErrorType.VALIDATION,
      undefined,
      undefined,
      context
    );
    this.name = 'BatchOperationError';
    this.records = records;
  }
}

/**
 * Interface for batch create operation
 */
export interface BatchCreatePayload {
  fields: Record<string, any>;
  typecast?: boolean;
}

/**
 * Interface for batch update operation
 */
export interface BatchUpdatePayload {
  id: string;
  fields: Record<string, any>;
  typecast?: boolean;
}

/**
 * Interface for batch upsert operation
 * Defines fields to find a record, or create if not found
 */
export interface BatchUpsertPayload {
  id?: string;
  fields: Record<string, any>;
  findFields?: Record<string, any>;
  typecast?: boolean;
}

/**
 * Generate endpoint identifier for retry
 */
function getEndpointId(tableName: string, operation: string): string {
  return `airtable:${tableName}:${operation}:batch`;
}

/**
 * Split array into chunks of specified size
 */
function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

/**
 * Base function to create multiple records in a single batch operation
 */
async function baseBatchCreate(
  tableName: string,
  records: BatchCreatePayload[]
): Promise<AirtableRecord[]> {
  if (records.length === 0) {
    return [];
  }
  
  // Chunk records to respect API limits
  const chunks = chunkArray(records, MAX_RECORDS_PER_BATCH);
  const results: AirtableRecord[] = [];
  const errors: Array<{ id?: string; error: Error }> = [];
  
  for (const chunk of chunks) {
    await respectRateLimit();
    
    try {
      const url = `${ENDPOINTS.REST_API}/${airtableConfig.baseId}/${encodeURIComponent(tableName)}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: airtableConfig.getAuthHeaders(),
        body: JSON.stringify({ records: chunk, typecast: chunk[0].typecast }),
      });
      
      const chunkResults = await processApiResponse<{ records: AirtableRecord[] }>(
        response, 
        { operation: 'batchCreate', tableName, chunkSize: chunk.length }
      );
      
      results.push(...chunkResults.records);
    } catch (error) {
      // Wrap the error for better context
      const wrappedError = wrapError(error, { 
        operation: 'batchCreate', 
        tableName, 
        chunk
      });
      
      // Log the error
      logAirtableError(wrappedError);
      
      if (isAirtableError(wrappedError)) {
        errors.push({ error: wrappedError });
      } else if (error instanceof Error) {
        errors.push({ error });
      } else {
        errors.push({ error: new Error(String(error)) });
      }
    }
  }
  
  // Invalidate cache
  await invalidateCache(`rest:${tableName}:`);
  
  // If there were errors, throw a batch error
  if (errors.length > 0) {
    throw new BatchOperationError(
      `Failed to create some records in batch for table ${tableName}`, 
      errors,
      { tableName, recordCount: records.length }
    );
  }
  
  return results;
}

/**
 * Base function to update multiple records in a single batch operation
 */
async function baseBatchUpdate(
  tableName: string,
  records: BatchUpdatePayload[]
): Promise<AirtableRecord[]> {
  if (records.length === 0) {
    return [];
  }
  
  // Chunk records to respect API limits
  const chunks = chunkArray(records, MAX_RECORDS_PER_BATCH);
  const results: AirtableRecord[] = [];
  const errors: Array<{ id?: string; error: Error }> = [];
  
  for (const chunk of chunks) {
    await respectRateLimit();
    
    try {
      const url = `${ENDPOINTS.REST_API}/${airtableConfig.baseId}/${encodeURIComponent(tableName)}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: airtableConfig.getAuthHeaders(),
        body: JSON.stringify({ records: chunk, typecast: chunk[0].typecast }),
      });
      
      const chunkResults = await processApiResponse<{ records: AirtableRecord[] }>(
        response, 
        { operation: 'batchUpdate', tableName, chunkSize: chunk.length }
      );
      
      results.push(...chunkResults.records);
    } catch (error) {
      // Wrap the error for better context
      const wrappedError = wrapError(error, { 
        operation: 'batchUpdate', 
        tableName, 
        chunk
      });
      
      // Log the error
      logAirtableError(wrappedError);
      
      const affectedIds = chunk.map(record => record.id);
      if (isAirtableError(wrappedError)) {
        errors.push({ id: affectedIds.join(','), error: wrappedError });
      } else if (error instanceof Error) {
        errors.push({ id: affectedIds.join(','), error });
      } else {
        errors.push({ id: affectedIds.join(','), error: new Error(String(error)) });
      }
    }
  }
  
  // Invalidate cache
  await invalidateCache(`rest:${tableName}:`);
  
  // If there were errors, throw a batch error
  if (errors.length > 0) {
    throw new BatchOperationError(
      `Failed to update some records in batch for table ${tableName}`, 
      errors,
      { tableName, recordCount: records.length }
    );
  }
  
  return results;
}

/**
 * Base function to find records by fields or create if not found (upsert)
 * This is a two-step process since Airtable doesn't have a native upsert
 */
async function baseBatchUpsert(
  tableName: string,
  records: BatchUpsertPayload[]
): Promise<AirtableRecord[]> {
  if (records.length === 0) {
    return [];
  }
  
  const results: AirtableRecord[] = [];
  const createRecords: BatchCreatePayload[] = [];
  const updateRecords: BatchUpdatePayload[] = [];
  const errors: Array<{ id?: string; error: Error }> = [];
  
  // First, handle records with IDs directly (update)
  const updateDirectly = records.filter(record => record.id);
  const findOrCreate = records.filter(record => !record.id && record.findFields);
  
  // Prepare direct updates
  for (const record of updateDirectly) {
    if (record.id) {
      updateRecords.push({
        id: record.id,
        fields: record.fields,
        typecast: record.typecast
      });
    }
  }
  
  // For find-or-create, first try to find matching records
  for (const record of findOrCreate) {
    if (!record.findFields) continue;
    
    try {
      // Build filter formula from findFields
      const conditions = Object.entries(record.findFields).map(([field, value]) => {
        if (typeof value === 'string') {
          // Escape quotes and handle string comparison
          return `{${field}} = "${value.replace(/"/g, '\\"')}"`;
        } else if (Array.isArray(value)) {
          // For arrays, check if field contains any of the values
          const arrayConditions = value.map(v => 
            `{${field}} = "${String(v).replace(/"/g, '\\"')}"`
          );
          return `OR(${arrayConditions.join(', ')})`;
        } else {
          // For numbers and booleans
          return `{${field}} = ${value}`;
        }
      });
      
      const filterFormula = conditions.length > 1 
        ? `AND(${conditions.join(', ')})` 
        : conditions[0];
      
      await respectRateLimit();
      
      const url = `${ENDPOINTS.REST_API}/${airtableConfig.baseId}/${encodeURIComponent(tableName)}?filterByFormula=${encodeURIComponent(filterFormula)}&maxRecords=1`;
      const response = await fetch(url, {
        headers: airtableConfig.getAuthHeaders(),
      });
      
      const data = await processApiResponse<AirtableResponse>(
        response, 
        { operation: 'batchUpsert:find', tableName, filterFormula }
      );
      
      if (data.records && data.records.length > 0) {
        // Found a match, update it
        const existingRecord = data.records[0];
        
        // Only update if needed
        if (needsUpdate(existingRecord, record.fields)) {
          updateRecords.push({
            id: existingRecord.id,
            fields: record.fields,
            typecast: record.typecast
          });
        } else {
          // No changes needed, just add to results
          results.push(existingRecord);
        }
      } else {
        // No match found, create a new record
        createRecords.push({
          fields: record.fields,
          typecast: record.typecast
        });
      }
    } catch (error) {
      // Wrap and log error
      const wrappedError = wrapError(error, { 
        operation: 'batchUpsert:find', 
        tableName,
        findFields: record.findFields
      });
      
      logAirtableError(wrappedError);
      
      // Add to errors
      errors.push({ error: wrappedError instanceof Error ? wrappedError : new Error(String(wrappedError)) });
    }
  }
  
  // Perform updates and creates
  try {
    if (updateRecords.length > 0) {
      const updateResults = await baseBatchUpdate(tableName, updateRecords);
      results.push(...updateResults);
    }
  } catch (error) {
    // Handle batch update errors
    if (error instanceof BatchOperationError) {
      errors.push(...error.records);
    } else {
      const wrappedError = wrapError(error, { 
        operation: 'batchUpsert:update', 
        tableName,
        recordCount: updateRecords.length
      });
      
      logAirtableError(wrappedError);
      errors.push({ error: wrappedError instanceof Error ? wrappedError : new Error(String(wrappedError)) });
    }
  }
  
  try {
    if (createRecords.length > 0) {
      const createResults = await baseBatchCreate(tableName, createRecords);
      results.push(...createResults);
    }
  } catch (error) {
    // Handle batch create errors
    if (error instanceof BatchOperationError) {
      errors.push(...error.records);
    } else {
      const wrappedError = wrapError(error, { 
        operation: 'batchUpsert:create', 
        tableName,
        recordCount: createRecords.length
      });
      
      logAirtableError(wrappedError);
      errors.push({ error: wrappedError instanceof Error ? wrappedError : new Error(String(wrappedError)) });
    }
  }
  
  // Invalidate cache
  await invalidateCache(`rest:${tableName}:`);
  
  // If there were errors, throw a batch error
  if (errors.length > 0) {
    throw new BatchOperationError(
      `Failed to upsert some records in batch for table ${tableName}`, 
      errors,
      { 
        tableName, 
        recordCount: records.length,
        updateCount: updateRecords.length,
        createCount: createRecords.length
      }
    );
  }
  
  return results;
}

/**
 * Base function to delete multiple records in a single batch operation
 */
async function baseBatchDelete(
  tableName: string,
  recordIds: string[]
): Promise<{ id: string; deleted: boolean }[]> {
  if (recordIds.length === 0) {
    return [];
  }
  
  // Chunk IDs to respect API limits
  const chunks = chunkArray(recordIds, MAX_RECORDS_PER_BATCH);
  const results: { id: string; deleted: boolean }[] = [];
  const errors: Array<{ id?: string; error: Error }> = [];
  
  for (const chunk of chunks) {
    await respectRateLimit();
    
    try {
      // Build comma-separated list of record IDs
      const recordsParam = chunk.join(',');
      const url = `${ENDPOINTS.REST_API}/${airtableConfig.baseId}/${encodeURIComponent(tableName)}?records=${recordsParam}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: airtableConfig.getAuthHeaders(),
      });
      
      const data = await processApiResponse<{ records: Array<{ id: string; deleted: boolean }> }>(
        response, 
        { operation: 'batchDelete', tableName, chunk }
      );
      
      results.push(...data.records);
    } catch (error) {
      // Wrap and log error
      const wrappedError = wrapError(error, { 
        operation: 'batchDelete', 
        tableName,
        chunk
      });
      
      logAirtableError(wrappedError);
      
      errors.push({ 
        id: chunk.join(','), 
        error: wrappedError instanceof Error ? wrappedError : new Error(String(wrappedError)) 
      });
    }
  }
  
  // Invalidate cache
  await invalidateCache(`rest:${tableName}:`);
  
  // If there were errors, throw a batch error
  if (errors.length > 0) {
    throw new BatchOperationError(
      `Failed to delete some records in batch for table ${tableName}`, 
      errors,
      { tableName, recordCount: recordIds.length }
    );
  }
  
  return results;
}

// ------------------- Enhanced API Functions (with retry) -------------------

/**
 * Create multiple records in a single batch operation with retry
 */
export async function batchCreate(
  tableName: string,
  records: BatchCreatePayload[]
): Promise<AirtableRecord[]> {
  const endpointId = getEndpointId(tableName, 'batchCreate');
  
  try {
    return await withRetry(
      () => baseBatchCreate(tableName, records),
      endpointId
    );
  } catch (error) {
    // For batch errors, maintain the BatchOperationError structure
    if (error instanceof BatchOperationError) {
      // Just add user-friendly message
      error.context = { 
        ...(error.context || {}), 
        userMessage: getUserFriendlyMessage(error) 
      };
      throw error;
    }
    
    // For other errors, wrap and add user-friendly message
    const wrappedError = wrapError(error, { 
      operation: 'batchCreate',
      tableName, 
      recordCount: records.length,
      userMessage: getUserFriendlyMessage(error)
    });
    
    logAirtableError(wrappedError);
    throw wrappedError;
  }
}

/**
 * Update multiple records in a single batch operation with retry
 */
export async function batchUpdate(
  tableName: string,
  records: BatchUpdatePayload[]
): Promise<AirtableRecord[]> {
  const endpointId = getEndpointId(tableName, 'batchUpdate');
  
  try {
    return await withRetry(
      () => baseBatchUpdate(tableName, records),
      endpointId
    );
  } catch (error) {
    // For batch errors, maintain the BatchOperationError structure
    if (error instanceof BatchOperationError) {
      // Just add user-friendly message
      error.context = { 
        ...(error.context || {}), 
        userMessage: getUserFriendlyMessage(error) 
      };
      throw error;
    }
    
    // For other errors, wrap and add user-friendly message
    const wrappedError = wrapError(error, { 
      operation: 'batchUpdate',
      tableName, 
      recordCount: records.length,
      userMessage: getUserFriendlyMessage(error)
    });
    
    logAirtableError(wrappedError);
    throw wrappedError;
  }
}

/**
 * Find records by fields or create if not found (upsert) with retry
 */
export async function batchUpsert(
  tableName: string,
  records: BatchUpsertPayload[]
): Promise<AirtableRecord[]> {
  const endpointId = getEndpointId(tableName, 'batchUpsert');
  
  try {
    return await withRetry(
      () => baseBatchUpsert(tableName, records),
      endpointId
    );
  } catch (error) {
    // For batch errors, maintain the BatchOperationError structure
    if (error instanceof BatchOperationError) {
      // Just add user-friendly message
      error.context = { 
        ...(error.context || {}), 
        userMessage: getUserFriendlyMessage(error) 
      };
      throw error;
    }
    
    // For other errors, wrap and add user-friendly message
    const wrappedError = wrapError(error, { 
      operation: 'batchUpsert',
      tableName, 
      recordCount: records.length,
      userMessage: getUserFriendlyMessage(error)
    });
    
    logAirtableError(wrappedError);
    throw wrappedError;
  }
}

/**
 * Delete multiple records in a single batch operation with retry
 */
export async function batchDelete(
  tableName: string,
  recordIds: string[]
): Promise<{ id: string; deleted: boolean }[]> {
  const endpointId = getEndpointId(tableName, 'batchDelete');
  
  try {
    return await withRetry(
      () => baseBatchDelete(tableName, recordIds),
      endpointId
    );
  } catch (error) {
    // For batch errors, maintain the BatchOperationError structure
    if (error instanceof BatchOperationError) {
      // Just add user-friendly message
      error.context = { 
        ...(error.context || {}), 
        userMessage: getUserFriendlyMessage(error) 
      };
      throw error;
    }
    
    // For other errors, wrap and add user-friendly message
    const wrappedError = wrapError(error, { 
      operation: 'batchDelete',
      tableName, 
      recordCount: recordIds.length,
      userMessage: getUserFriendlyMessage(error)
    });
    
    logAirtableError(wrappedError);
    throw wrappedError;
  }
}

/**
 * Determine if an existing record needs to be updated
 * Compares fields to avoid unnecessary API calls
 */
export function needsUpdate(existingRecord: AirtableRecord, newFields: Record<string, any>): boolean {
  // Check each field in newFields
  for (const [key, newValue] of Object.entries(newFields)) {
    const existingValue = existingRecord.fields[key];
    
    // Deep comparison for arrays and objects
    if (Array.isArray(newValue) && Array.isArray(existingValue)) {
      // Compare arrays (order matters for Airtable)
      if (newValue.length !== existingValue.length) {
        return true;
      }
      
      for (let i = 0; i < newValue.length; i++) {
        if (JSON.stringify(newValue[i]) !== JSON.stringify(existingValue[i])) {
          return true;
        }
      }
    }
    // Compare objects
    else if (
      typeof newValue === 'object' && 
      newValue !== null && 
      typeof existingValue === 'object' && 
      existingValue !== null
    ) {
      if (JSON.stringify(newValue) !== JSON.stringify(existingValue)) {
        return true;
      }
    }
    // Simple value comparison
    else if (newValue !== existingValue) {
      return true;
    }
  }
  
  return false;
} 
